<script setup>
/**
 * The assurance block: it sits directly under the hero because a parent needs to
 * see where our tutors studied before they are asked for anything.
 *
 * Everything is greyscale by default and returns to full colour on hover and on
 * focus, so the row reads as one object rather than seven competing heraldic
 * palettes.
 *
 * Six of these are coats of arms — portrait, roughly 5:6 — and UCL's is its
 * wordmark lockup, which is three and a half times wider than it is tall.
 * Matching them on height would make UCL four times the visual weight of the
 * rest, so each carries its own height class and they are balanced by area.
 * That is how a real logo strip is set.
 */
const universities = [
  { name: 'University of Oxford', file: 'oxford', height: 'h-14 md:h-16' },
  { name: 'University of Cambridge', file: 'cambridge', height: 'h-14 md:h-16' },
  { name: 'Imperial College London', file: 'imperial', height: 'h-14 md:h-16' },
  { name: 'London School of Economics', file: 'lse', height: 'h-14 md:h-16' },
  { name: 'Durham University', file: 'durham', height: 'h-14 md:h-16' },
  { name: 'University of Warwick', file: 'warwick', height: 'h-14 md:h-16' },
  { name: 'UCL', file: 'ucl', height: 'h-9 md:h-10' },
]
</script>

<template>
  <section class="pb-12 md:pb-16">
    <div class="container-page text-center">
      <p v-reveal class="text-xs font-extrabold uppercase tracking-[0.18em] text-brand">
        Top tutors. Top universities.
      </p>
      <p v-reveal="{ delay: 60 }" class="mx-auto mt-2 max-w-md text-sm text-muted">
        Our tutors graduate from and study at the UK’s leading universities.
      </p>

      <ul class="mt-9 grid grid-cols-2 items-end gap-x-6 gap-y-8 sm:grid-cols-4 sm:gap-x-10 lg:grid-cols-7">
        <li
          v-for="(uni, index) in universities"
          :key="uni.file"
          v-reveal="{ delay: 80 + index * 50 }"
          class="group flex h-full flex-col items-center justify-end gap-2"
        >
          <span class="flex h-16 items-end md:h-[68px]">
            <img
              :src="`/brand/universities/${uni.file}.webp`"
              :alt="uni.name"
              loading="lazy"
              decoding="async"
              :class="[
                uni.height,
                'w-auto grayscale opacity-70 transition duration-300 ease-brand',
                'group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105',
                'group-focus-within:grayscale-0 group-focus-within:opacity-100',
              ]"
            />
          </span>
          <span class="text-[11px] font-semibold leading-tight text-subtle transition-colors duration-300 group-hover:text-muted">
            {{ uni.name }}
          </span>
        </li>
      </ul>
    </div>
  </section>
</template>
